import type { PendingVisit } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UnsavedChangesGuard {
  /** Re-runs the intercepted visit, leaving the pending changes behind. */
  confirmLeave: () => void;
  isDialogOpen: boolean;
  /** Dismisses the prompt and stays on the page. */
  stay: () => void;
}

/** Partial reloads and prefetches keep the user on the page; form submissions carry the changes. */
function isNavigation(visit: PendingVisit): boolean {
  return visit.method === "get" && !visit.prefetch && visit.only.length === 0 && visit.except.length === 0;
}

/**
 * Intercepts Inertia navigations away from a page with unsaved changes and asks
 * for confirmation first. Browser history moves are not interceptable — Inertia
 * restores those without firing `before`, so back/forward is not guarded.
 */
export function useUnsavedChangesGuard(isDirty: boolean): UnsavedChangesGuard {
  const [pendingVisit, setPendingVisit] = useState<PendingVisit | null>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const bypassRef = useRef(false);

  useEffect(
    () =>
      router.on("before", (event) => {
        if (bypassRef.current) {
          bypassRef.current = false;
          return;
        }
        if (!isDirtyRef.current || !isNavigation(event.detail.visit)) return;

        event.preventDefault();
        setPendingVisit(event.detail.visit);
      }),
    [],
  );

  useEffect(() => {
    if (!isDirty) return;

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const confirmLeave = useCallback(() => {
    if (!pendingVisit) return;

    bypassRef.current = true;
    setPendingVisit(null);
    router.visit(pendingVisit.url, {
      data: pendingVisit.data,
      headers: pendingVisit.headers,
      method: pendingVisit.method,
      preserveScroll: pendingVisit.preserveScroll,
      preserveState: pendingVisit.preserveState,
      replace: pendingVisit.replace,
    });
  }, [pendingVisit]);

  return {
    confirmLeave,
    isDialogOpen: pendingVisit !== null,
    stay: useCallback(() => setPendingVisit(null), []),
  };
}
